<template>
  <Layout>
    <PageHeader :title="title" :items="items"/>
    <Aguarde :show="httpSend && !carregando"></Aguarde>

    <div class="notif-sn">

      <!-- ─── Barra superior ─────────────────────────────────── -->
      <div class="page-intro">
        <p class="page-intro__sub">
          Selecione as competências pendentes para notificação. As já notificadas ficam bloqueadas.
        </p>
        <div class="page-intro__actions">
          <span v-if="ultimaAtualizacao && !carregando" class="badge-live">
            <span class="live-dot"/>
            Atualizado às {{ ultimaAtualizacao }}
          </span>
          <button type="button" class="btn-atualizar" :disabled="httpSend" @click="pesquisar">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
              <path d="M14 8a6 6 0 11-1.76-4.24" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M14 2v4h-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                    stroke-linejoin="round"/>
            </svg>
            {{ httpSend ? 'Atualizando...' : 'Atualizar' }}
          </button>
        </div>
      </div>

      <!-- ─── Erro na consulta ───────────────────────────────── -->
      <div v-if="erro" class="alert-erro">
        <div class="alert-erro__icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9"/>
            <path d="M12 8v5M12 16h.01"/>
          </svg>
        </div>
        <div class="alert-erro__body">
          <p class="alert-erro__title">Não foi possível carregar as notificações</p>
          <p class="alert-erro__desc">{{ erro }}</p>
        </div>
        <button type="button" class="alert-erro__btn" :disabled="httpSend" @click="pesquisar">Tentar novamente</button>
      </div>

      <!-- ─── Skeleton de carregamento ───────────────────────── -->
      <div v-if="carregando" class="skeleton-wrap">
        <div class="sk-metrics">
          <div v-for="n in 5" :key="`skm-${n}`" class="sk-metric"/>
        </div>
        <div class="sk-cards">
          <div v-for="n in 4" :key="`skc-${n}`" class="sk-card"/>
        </div>
      </div>

      <template v-else>

        <!-- ─── Indicadores ──────────────────────────────────── -->
        <div class="metrics-grid">
          <div v-for="indicador in indicadores" :key="indicador.key" class="metric-card"
               :class="`metric-card--${indicador.color}`">
            <div class="metric-icon">
              <span v-html="indicador.icon"/>
            </div>
            <div class="metric-body">
              <p class="metric-label">{{ indicador.label }}</p>
              <p class="metric-value">{{ indicador.value }}</p>
              <p class="metric-sub">{{ indicador.sub }}</p>
            </div>
          </div>
        </div>

        <!-- ─── Filtros de pesquisa ──────────────────────────── -->
        <div class="table-card mb-section">
          <div class="table-header">
            <div class="header-left-group">
              <p class="chart-title">Filtros de Pesquisa</p>
              <span v-if="filtrosAtivos > 0" class="badge-count badge-count--ativo">
                {{ filtrosAtivos }} {{ filtrosAtivos === 1 ? 'filtro ativo' : 'filtros ativos' }}
              </span>
            </div>
          </div>
          <div class="filtros-body">
            <b-form>
              <div class="filtros-grid">
                <div class="campo">
                  <label for="competencia">Exercício</label>
                  <select id="competencia" v-model="filtro.exercicio" @change="pesquisar()" class="campo-input">
                    <option v-for="competencia in listaExercicio" :key="competencia.exercicio"
                            :value="competencia.exercicio">
                      {{ competencia.exercicio }}
                    </option>
                  </select>
                </div>
                <div class="campo">
                  <label for="pes_nome">Razão Social</label>
                  <input id="pes_nome" v-model="filtro.pes_nome" type="text" class="campo-input"
                         placeholder="Razão social ou nome" autocomplete="off">
                </div>
                <div class="campo">
                  <label for="esr_cnpj">CNPJ</label>
                  <the-mask id="esr_cnpj" class="campo-input" v-model="filtro.esr_cnpj"
                            :mask="['###.###.###-##', '##.###.###/####-##']" placeholder="Documento"/>
                </div>
              </div>
              <div class="filtros-acoes">
                <button type="button" @click="limparFiltros()" class="btn-limpar">
                  <i class="bx bx-eraser"></i> Limpar
                </button>
              </div>
            </b-form>
          </div>
        </div>

        <!-- ─── Lista de empresas ────────────────────────────── -->
        <div class="table-card">
          <div class="table-header">
            <div class="header-left-group">
              <p class="chart-title">Empresas</p>
              <span class="badge-count">
                {{ formatInteiro(registrosFiltrados.length) }}
                {{ registrosFiltrados.length === 1 ? 'registro' : 'registros' }}
              </span>
            </div>
            <div v-if="registrosFiltrados.length > 0" class="header-right-group">
              <button type="button" class="btn-acao" :disabled="totalPendentes === 0"
                      @click="selecionarPendentesVisiveis">
                Selecionar pendentes visíveis
              </button>
              <button type="button" class="btn-acao" :disabled="!temSelecao" @click="limparSelecao">
                Limpar seleção
              </button>
            </div>
          </div>

          <div v-if="registrosPagina.length > 0" ref="lista" class="empresas-grid">
            <article v-for="registro in registrosPagina" :key="registro.chave" class="empresa-card"
                     :class="{ 'empresa-card--ativa': registro.selecionadas > 0 }">

              <header class="empresa-card__header">
                <div class="empresa-card__id">
                  <p class="empresa-card__nome" :title="registro.nomeExibicao">{{ registro.nomeExibicao }}</p>
                  <p class="empresa-card__dados">
                    <span>CNPJ: {{ registro.cnpjFormatado }}</span>
                    <span class="empresa-card__sep">•</span>
                    <span>Econômico: {{ registro.eco_codigo === null ? '—' : registro.eco_codigo }}</span>
                  </p>
                </div>
                <span class="badge-selecao" :class="{ 'badge-selecao--ativa': registro.selecionadas > 0 }">
                  {{ registro.selecionadas }}/{{ registro.pendentes.length }}
                </span>
              </header>

              <!-- Resumo da empresa -->
              <div class="empresa-resumo">
                <div class="empresa-resumo__item">
                  <span class="empresa-resumo__valor">{{ registro.qtdTotal }}</span>
                  <span class="empresa-resumo__rotulo">Total</span>
                </div>
                <div class="empresa-resumo__item empresa-resumo__item--notificadas">
                  <span class="empresa-resumo__valor">{{ registro.qtdNotificadas }}</span>
                  <span class="empresa-resumo__rotulo">Notificadas</span>
                </div>
                <div class="empresa-resumo__item empresa-resumo__item--pendentes">
                  <span class="empresa-resumo__valor">{{ registro.qtdPendentes }}</span>
                  <span class="empresa-resumo__rotulo">Pendentes</span>
                </div>
              </div>

              <div v-if="registro.competencias.length > 0" class="empresa-card__body">
                <div class="empresa-card__subheader">
                  <span class="empresa-card__legenda">Competências</span>
                  <div class="empresa-card__acoes">
                    <button type="button" class="btn-link"
                            :disabled="registro.todasPendentesSelecionadas || !registro.selecionavel
                                       || registro.pendentes.length === 0"
                            @click="selecionarPendentesDaEmpresa(registro)">
                      Selecionar pendentes
                    </button>
                    <span class="btn-link__sep">·</span>
                    <button type="button" class="btn-link" :disabled="registro.selecionadas === 0"
                            @click="limparEmpresa(registro)">
                      Limpar seleção
                    </button>
                  </div>
                </div>

                <div class="competencias-grid">
                  <label v-for="competencia in registro.competencias"
                         :key="`${registro.chave}-${competencia.competencia}`"
                         class="comp-chip"
                         :class="{
                           'comp-chip--ativa': competencia.selecionada,
                           'comp-chip--notificada': competencia.notificado,
                           'comp-chip--bloqueada': competencia.bloqueada
                         }"
                         :title="competencia.notificado
                                 ? `Notificada em ${competencia.nts_data || 'data não informada'}`
                                 : 'Competência pendente de notificação'">
                    <input type="checkbox" class="comp-chip__input"
                           :checked="competencia.notificado || competencia.selecionada"
                           :disabled="competencia.bloqueada"
                           @change="alternarCompetencia(registro, competencia)">
                    <span class="comp-chip__box">
                      <svg v-if="competencia.notificado" width="9" height="9" viewBox="0 0 24 24" fill="none">
                        <rect x="4" y="10" width="16" height="11" rx="2" stroke="currentColor" stroke-width="2.4"/>
                        <path d="M8 10V7a4 4 0 018 0v3" stroke="currentColor" stroke-width="2.4"
                              stroke-linecap="round"/>
                      </svg>
                      <svg v-else width="9" height="9" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6.2l2.6 2.6L10 3.4" stroke="currentColor" stroke-width="2"
                              stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </span>
                    <span class="comp-chip__label">{{ competencia.competencia }}</span>
                    <span class="comp-chip__status"
                          :class="competencia.notificado ? 'comp-chip__status--notificada' : 'comp-chip__status--pendente'">
                      {{ competencia.notificado ? 'Notificada' : 'Pendente' }}
                    </span>
                  </label>
                </div>

                <!-- Notificações já realizadas, agrupadas por nts_codigo -->
                <div v-if="registro.notificacoesRealizadas.length > 0" class="notificacoes-bloco">
                  <span class="empresa-card__legenda">Notificações realizadas</span>
                  <div v-for="notificacao in registro.notificacoesRealizadas" :key="`nts-${notificacao.nts_codigo}`"
                       class="notificacao-item">
                    <div class="notificacao-item__topo">
                      <span class="notificacao-item__numero">Notificação #{{ notificacao.nts_codigo }}</span>
                      <span class="notificacao-item__data">
                        {{ notificacao.nts_data || 'Data não informada' }}
                      </span>
                    </div>
                    <p class="notificacao-item__competencias">
                      Competências: {{ notificacao.competenciasTexto }}
                    </p>
                  </div>
                </div>

                <footer class="empresa-card__footer">
                  <span v-if="!registro.selecionavel" class="empresa-card__aviso">
                    Empresa sem código econômico — não é possível notificar.
                  </span>
                  <span v-else-if="registro.pendentes.length === 0" class="empresa-card__aviso">
                    Todas as competências já foram notificadas.
                  </span>
                  <span v-else>Selecionadas: <strong>{{ registro.selecionadas }}</strong></span>
                </footer>
              </div>

              <div v-else class="empresa-card__vazia">
                Nenhuma competência válida retornada para esta empresa.
              </div>
            </article>
          </div>

          <!-- Estado vazio -->
          <div v-else class="empty-state">
            <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
              <circle cx="18" cy="18" r="17" stroke="#e2e8f0" stroke-width="1.5"/>
              <path d="M12 18h12M18 12v12" stroke="#cbd5e1" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <p v-if="registros.length === 0">Nenhuma empresa a notificar</p>
            <p v-else>Nenhuma empresa encontrada para os filtros aplicados</p>
            <button v-if="registros.length > 0 && filtrosAtivos > 0" type="button" class="btn-limpar"
                    @click="limparFiltros">
              Limpar filtros
            </button>
          </div>

          <div v-if="registrosFiltrados.length > 0" class="table-footer">
            <span class="table-footer__info">
              Exibindo {{ formatInteiro(primeiroDaPagina) }}–{{ formatInteiro(ultimoDaPagina) }}
              de {{ formatInteiro(registrosFiltrados.length) }}
            </span>
            <paginate
                v-if="totalPaginas > 1"
                v-model="pagina"
                :page-count="totalPaginas"
                :page-range="3"
                :margin-pages="2"
                :click-handler="irParaPagina"
                :prev-text="'Anterior'"
                :next-text="'Próxima'"
                :prev-link-class="'page-link'"
                :next-link-class="'page-link'"
                :page-link-class="'page-link'"
                :container-class="'pagination pagination-rounded mb-0'"
                :page-class="'page-item'">
            </paginate>
          </div>
        </div>

        <!-- ─── Resumo e envio ───────────────────────────────── -->
        <div class="action-bar">
          <div class="action-bar__resumo">
            <span class="resumo-item">
              <strong>{{ formatInteiro(totalCompetenciasSelecionadas) }}</strong>
              {{ totalCompetenciasSelecionadas === 1 ? 'competência selecionada' : 'competências selecionadas' }}
            </span>
            <span class="resumo-sep"/>
            <span class="resumo-item">
              <strong>{{ formatInteiro(totalEmpresasSelecionadas) }}</strong>
              {{ totalEmpresasSelecionadas === 1 ? 'empresa selecionada' : 'empresas selecionadas' }}
            </span>
            <span class="resumo-sep"/>
            <span class="resumo-item">
              <strong>{{ formatInteiro(totalPendentes) }}</strong>
              {{ totalPendentes === 1 ? 'competência pendente' : 'competências pendentes' }}
            </span>
          </div>
          <button type="button" class="btn-enviar" :disabled="!podeEnviar || httpSend"
                  @click="openModal('modal-gera-arquivo')">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M14.5 1.5L7.5 8.5M14.5 1.5l-4.4 13-2.6-6-6-2.6 13-4.4z" stroke="currentColor"
                    stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Gerar Arquivo de Notificação
            <span v-if="totalCompetenciasSelecionadas > 0" class="btn-enviar__badge">
              {{ formatInteiro(totalCompetenciasSelecionadas) }}
            </span>
          </button>
        </div>
      </template>
    </div>
    <b-modal
        id="modal-gera-arquivo"
        ref="modal-gera-arquivo"
        size="lg"
        title="Gerar Arquivo de Notificação"
        title-class="font-18"
        :hide-header-close="true"
        :no-close-on-backdrop="true"
        :no-close-on-esc="true">
      <div class="modal-body">
        <div class="row">
          <div class="col-3">
            <b-form-group label="Lote" label-for="cert_senha">
              <input id="cert_senha" v-model="lote.nts_lote"
                     :class="{ 'is-invalid': $v.lote.nts_lote.$error }"
                     autocorrect="off" autocapitalize="off" autocomplete="off"
                     spellcheck="false" minlength="1" maxlength="6" type="text" class="form-control">
              <div v-if="$v.lote.nts_lote.$error" class="invalid-feedback">
                Informe o número do lote
              </div>
            </b-form-group>
          </div>

          <div class="col-9">
            <b-form-group label="Nome do Fiscal" label-for="nome_fiscal">
              <input id="nome_fiscal" disabled v-model="lote.nome_fiscal" autocorrect="off" autocapitalize="off"
                     autocomplete="off"
                     spellcheck="false" type="text" class="form-control">
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-3">
            <b-form-group label="Matrícula" label-for="auf_matricula">
              <input id="auf_matricula" v-model="lote.auf_matricula" autocorrect="off" autocapitalize="off"
                     :class="{ 'is-invalid': $v.lote.auf_matricula.$error }"
                     autocomplete="off"
                     spellcheck="false" minlength="1" maxlength="10" type="text" class="form-control">
              <div v-if="$v.lote.auf_matricula.$error" class="invalid-feedback">
                Informe a matrícula
              </div>
            </b-form-group>
          </div>
          <div class="col-6">
            <b-form-group label="Cargo do Fiscal" label-for="auf_cargo">
              <input id="auf_cargo" v-model="lote.auf_cargo" autocorrect="off" autocapitalize="off" autocomplete="off"
                     :class="{ 'is-invalid': $v.lote.auf_cargo.$error }"
                     spellcheck="false" minlength="1" maxlength="60" type="text" class="form-control">
              <div v-if="$v.lote.auf_cargo.$error" class="invalid-feedback">
                Informe o cargo
              </div>
            </b-form-group>
          </div>
          <div class="col-3">
            <b-form-group label="Prazo (dias)" label-for="nts_prazo">
              <input id="nts_prazo" v-model="lote.nts_prazo" autocorrect="off" autocapitalize="off" autocomplete="off"
                     :class="{ 'is-invalid': $v.lote.nts_prazo.$error }"
                     min="1"
                     spellcheck="false" type="number" class="form-control">
              <div v-if="$v.lote.auf_cargo.$error" class="invalid-feedback">
                Informe o prazo em dias
              </div>
            </b-form-group>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <b-form-group label="Texto Livre" label-for="nts_texto_livre">
              <textarea id="nts_texto_livre"
                        v-model="lote.nts_texto_livre"
                        :class="{ 'is-invalid': $v.lote.nts_texto_livre.$error }"
                        autocorrect="off"
                        minlength="1"
                        maxlength="900"
                        autocapitalize="off"
                        autocomplete="off"
                        spellcheck="false" type="text" class="form-control">
              </textarea>
              <div v-if="$v.lote.nts_texto_livre.$error" class="invalid-feedback">
                Informe o texto livre
              </div>
            </b-form-group>
          </div>
          <div class="col-12">
            <b-form-group label="Base Legal" label-for="nts_base_legal">
              <textarea id="nts_base_legal"
                        v-model="lote.nts_base_legal"
                        :class="{ 'is-invalid': $v.lote.nts_base_legal.$error }"
                        autocorrect="off"
                        maxlength="200"
                        autocapitalize="off"
                        autocomplete="off"
                        spellcheck="false" type="text" class="form-control">
              </textarea>
              <div v-if="$v.lote.nts_base_legal.$error" class="invalid-feedback">
                Informe a base legal
              </div>
            </b-form-group>
          </div>
        </div>


      </div>
      <template #modal-footer="{ ok, cancel}">
        <button v-if="httpSend" type="button" disabled class="btn btn-label btn-sm mr-2 mb-2 btn-primary"><i
            class="bx bx-loader bx-spin font-size-16 align-middle mr-2"></i> Processando
        </button>
        <button v-else @click="enviarNotificacoes" type="button" title="Gera Arquivo"
                class="btn btn-label btn-sm mr-2 mb-2 btn-primary"><i
            class="bx bx-save label-icon"></i> Gera Arquivo
        </button>
        <button type="button" title="Cancelar"
                @click="closeModal('modal-gera-arquivo')"
                class="btn btn-label btn-sm mr-2 mb-2 btn-outline-danger"><i
            class="bx bx-block label-icon"></i> Cancelar
        </button>
      </template>
    </b-modal>
  </Layout>
</template>

<script>
import Paginate from 'vuejs-paginate';
import Layout from "@/router/layouts/main";
import PageHeader from "@/components/page-header";
import Aguarde from "@/components/widgets/aguarde.vue";
import {TheMask} from 'vue-the-mask';
import {authMethods} from "@/state/helpers";
import appConfig from "@/dados/app.config.json";
import {list_simples_nacional_notificacoes, save_nacional_notificacoes} from "@/api/admin/fiscalizacao/notificacoes";
import {notifyErro, notifySucesso} from "@/helps/notificacao";
import {openDownloadFile} from "@/api/core/download";
import {required} from "vuelidate/lib/validators";

const POR_PAGINA = 1000;

export default {
  page: {
    title: "Notificações Simples Nacional",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
    Layout,
    PageHeader,
    Aguarde,
    Paginate,
    TheMask
  },
  data() {
    return {
      title: "Notificações Simples Nacional",
      items: [
        {
          text: 'Painel',
          href: '/painel',
        },
        {
          text: 'Notificações Simples Nacional',
          active: true,
        },
      ],
      httpSend: false,
      carregando: true,
      erro: null,
      ultimaAtualizacao: null,
      filtro: {
        pes_nome: "",
        esr_cnpj: "",
        competencia: "",
        selecao: "todas",
        exercicio: "0",
      },
      listaExercicio: [],
      empresas: [],
      notificacoes_enviar: [],
      por_pagina: POR_PAGINA,
      pagina: 1,
      lote: {
        nts_lote: "",
        nts_prazo: 30,
        nts_texto_livre: "Corrigir as divergência constatadas entre a base de cálculo informada no PGDAS-D e o montante de serviços tributáveis apurado via NFS-e emitidas nas competências constantes em nossos registros.",
        nts_base_legal: "Esta inconsistência descumpre as regras de apuração da receita bruta e tributação estabelecidas nos arts. 18 e 26, I, da LC nº 123/2006, e nos arts. 16 a 19 e 25 a 26 da Resolução CGSN nº 140/2018.",
        nome_fiscal: "",
        auf_matricula: "",
        auf_cargo: ""
      }
    };
  },
  validations: {
    lote: {
      nts_lote: {
        required,
      },
      nts_prazo: {
        required,
      },
      nts_texto_livre: {
        required,
      },
      nts_base_legal: {
        required,
      },
      nome_fiscal: {
        required,
      },
      auf_matricula: {
        required,
      },
      auf_cargo: {
        required,
      }
    },
  },
  computed: {
    // ─── Normalização dos dados da API ────────────────────
    // O JSON.parse das competências acontece uma única vez por carga:
    // este computed só reavalia quando `empresas` muda.
    registros() {
      return (this.empresas || []).map((item, indice) => {
        const codigo = this.paraCodigo(item.eco_codigo);
        const cnpj = this.somenteDigitos(item.esr_cnpj);
        const nome = this.textoOuNulo(item.pes_nome);
        const nomeExibicao = nome || 'Empresa não cadastrada';
        const competencias = this.extrairCompetencias(item.competencias);

        const pendentes = [];
        const competenciasLista = [];
        let notificadasDerivadas = 0;
        competencias.forEach(competencia => {
          competenciasLista.push(competencia.competencia);
          if (competencia.notificado) notificadasDerivadas++;
          else pendentes.push(competencia.competencia);
        });

        // Os contadores da API prevalecem; a contagem local é o fallback.
        const qtdNotificadas = this.paraInteiroOuNulo(item.qtd_notificadas);
        const qtdPendentes = this.paraInteiroOuNulo(item.qtd_pendentes);
        const notificadas = qtdNotificadas === null ? notificadasDerivadas : qtdNotificadas;
        const abertas = qtdPendentes === null ? pendentes.length : qtdPendentes;

        return {
          chave: `${codigo === null ? 'sem-codigo' : codigo}-${indice}`,
          eco_codigo: codigo,
          esr_cnpj: cnpj,
          cnpjFormatado: this.formatCnpj(cnpj),
          nome,
          nomeExibicao,
          // Sem o código econômico não há como montar a notificação.
          selecionavel: codigo !== null,
          competencias,
          competenciasLista,
          pendentes,
          qtdNotificadas: notificadas,
          qtdPendentes: abertas,
          qtdTotal: notificadas + abertas,
          notificacoesRealizadas: this.agruparNotificacoes(competencias),
          buscavel: this.normalizarTexto(nomeExibicao),
        };
      });
    },
    // Índice rápido do estado de seleção: {eco_codigo: Set('MM/AAAA')}
    mapaSelecao() {
      const mapa = {};
      this.notificacoes_enviar.forEach(notificacao => {
        mapa[notificacao.eco_codigo] = new Set(notificacao.competencias);
      });
      return mapa;
    },
    // Competências que podem ser selecionadas: {eco_codigo: Set('MM/AAAA')}
    // É o filtro que impede uma competência já notificada de entrar na seleção.
    pendentesPorEmpresa() {
      const mapa = {};
      this.registros.forEach(registro => {
        if (registro.eco_codigo === null) return;
        if (!mapa[registro.eco_codigo]) mapa[registro.eco_codigo] = new Set();
        registro.pendentes.forEach(competencia => mapa[registro.eco_codigo].add(competencia));
      });
      return mapa;
    },
    // ─── Filtros ──────────────────────────────────────────
    registrosFiltrados() {
      const nome = this.normalizarTexto(this.filtro.pes_nome);
      const cnpj = this.somenteDigitos(this.filtro.esr_cnpj);
      const competencia = this.filtro.competencia;
      const selecao = this.filtro.selecao;

      return this.registros.filter(registro => {
        if (nome && registro.buscavel.indexOf(nome) === -1) return false;
        if (cnpj && registro.esr_cnpj.indexOf(cnpj) === -1) return false;
        if (competencia && registro.competenciasLista.indexOf(competencia) === -1) return false;
        if (selecao !== 'todas') {
          const temSelecionadas = this.totalSelecionadasDaEmpresa(registro) > 0;
          if (selecao === 'selecionadas' && !temSelecionadas) return false;
          if (selecao === 'nao_selecionadas' && temSelecionadas) return false;
        }
        return true;
      });
    },
    filtrosAtivos() {
      let total = 0;
      if (this.filtro.pes_nome) total++;
      if (this.filtro.esr_cnpj) total++;
      if (this.filtro.competencia) total++;
      if (this.filtro.selecao !== 'todas') total++;
      return total;
    },
    // ─── Paginação ────────────────────────────────────────
    // Cada competência já sai daqui com o estado pronto para renderizar,
    // evitando chamadas de método por checkbox dentro do template.
    registrosPagina() {
      const inicio = (this.pagina - 1) * this.por_pagina;
      return this.registrosFiltrados
          .slice(inicio, inicio + this.por_pagina)
          .map(registro => {
            const selecionadas = this.mapaSelecao[registro.eco_codigo];
            let total = 0;
            const competencias = registro.competencias.map(competencia => {
              const marcada = !competencia.notificado && !!selecionadas && selecionadas.has(competencia.competencia);
              if (marcada) total++;
              return {
                ...competencia,
                selecionada: marcada,
                bloqueada: competencia.notificado || !registro.selecionavel,
              };
            });
            return {
              ...registro,
              competencias,
              selecionadas: total,
              todasPendentesSelecionadas: registro.pendentes.length > 0 && total === registro.pendentes.length,
            };
          });
    },
    totalPaginas() {
      return Math.ceil(this.registrosFiltrados.length / this.por_pagina) || 0;
    },
    primeiroDaPagina() {
      if (!this.registrosFiltrados.length) return 0;
      return (this.pagina - 1) * this.por_pagina + 1;
    },
    ultimoDaPagina() {
      return Math.min(this.pagina * this.por_pagina, this.registrosFiltrados.length);
    },
    // ─── Totalizadores ────────────────────────────────────
    totalCompetencias() {
      return this.registrosFiltrados.reduce((total, registro) => total + registro.qtdTotal, 0);
    },
    totalNotificadas() {
      return this.registrosFiltrados.reduce((total, registro) => total + registro.qtdNotificadas, 0);
    },
    totalPendentes() {
      return this.registrosFiltrados.reduce((total, registro) => total + registro.qtdPendentes, 0);
    },
    totalCompetenciasSelecionadas() {
      return this.notificacoes_enviar.reduce((total, notificacao) => total + notificacao.competencias.length, 0);
    },
    totalEmpresasSelecionadas() {
      return this.notificacoes_enviar.length;
    },
    temSelecao() {
      return this.notificacoes_enviar.length > 0;
    },
    podeEnviar() {
      return this.temSelecao;
    },
    indicadores() {
      return [
        {
          key: 'empresas',
          label: 'Empresas',
          value: this.formatInteiro(this.registrosFiltrados.length),
          sub: this.filtrosAtivos > 0
              ? `de ${this.formatInteiro(this.registros.length)} retornadas`
              : 'retornadas pela consulta',
          icon: this.iconEmpresa(),
          color: 'blue',
        },
        {
          key: 'total',
          label: 'Total de competências',
          value: this.formatInteiro(this.totalCompetencias),
          sub: 'notificadas + pendentes',
          icon: this.iconCalendario(),
          color: 'slate',
        },
        {
          key: 'notificadas',
          label: 'Notificadas',
          value: this.formatInteiro(this.totalNotificadas),
          sub: 'já enviadas ao contribuinte',
          icon: this.iconCadeado(),
          color: 'teal',
        },
        {
          key: 'pendentes',
          label: 'Pendentes',
          value: this.formatInteiro(this.totalPendentes),
          sub: 'aguardando notificação',
          icon: this.iconAlerta(),
          color: 'amber',
        },
        {
          key: 'selecionadas',
          label: 'Selecionadas',
          value: this.formatInteiro(this.totalCompetenciasSelecionadas),
          sub: this.totalEmpresasSelecionadas === 1
              ? 'em 1 empresa'
              : `em ${this.formatInteiro(this.totalEmpresasSelecionadas)} empresas`,
          icon: this.iconCheck(),
          color: 'indigo',
        },
      ];
    },
  },
  watch: {
    filtro: {
      deep: true,
      handler() {
        this.pagina = 1;
      },
    },
    totalPaginas(valor) {
      if (valor > 0 && this.pagina > valor) this.pagina = valor;
    },
  },
  created() {
    this.pesquisar();
    this.lote.nome_fiscal = this.$store.state.auth.currentUser.pes_nome;
  },
  methods: {
    ...authMethods,
    pesquisar() {
      this.httpSend = true;
      this.erro = null;
      list_simples_nacional_notificacoes(this.filtro.exercicio).then(result => {
        if (result && result.status) {
          this.lote.nts_lote = result.dados.novo_lote;
          if (result.dados.fiscal) {
            this.lote.auf_matricula = result.dados.fiscal.auf_matricula
            this.lote.auf_cargo = result.dados.fiscal.auf_cargo
          }
          this.empresas = Array.isArray(result.dados.divergencias) ? result.dados.divergencias : [];
          this.listaExercicio = Array.isArray(result.dados.exercicios) ? result.dados.exercicios : [];
          this.filtro.exercicio = result.dados.exercicios_consulta;
          this.ultimaAtualizacao = new Date().toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'});
        } else {
          this.empresas = [];
          this.erro = (result && result.mensagem) || 'A consulta não retornou dados válidos.';
        }
      }).catch(error => {
        console.error(error);
        this.empresas = [];
        this.erro = 'Falha de comunicação com o servidor. Verifique sua conexão e tente novamente.';
      }).finally(() => {
        this.httpSend = false;
        this.carregando = false;
        this.sincronizarSelecao();
      });
    },
    // ─── Conversão e formatação ───────────────────────────
    textoOuNulo(valor) {
      if (valor === null || valor === undefined) return null;
      const texto = String(valor).trim();
      return texto === '' ? null : texto;
    },
    /** Converte o código econômico para inteiro; retorna null quando inválido. */
    paraCodigo(valor) {
      if (valor === null || valor === undefined || valor === '') return null;
      const numero = parseInt(valor, 10);
      return isNaN(numero) ? null : numero;
    },
    /** Contadores da API chegam como string ("2"); null quando ausentes ou inválidos. */
    paraInteiroOuNulo(valor) {
      if (valor === null || valor === undefined || valor === '') return null;
      const numero = parseInt(valor, 10);
      return isNaN(numero) ? null : numero;
    },
    /** Aceita true, "true", 1 e "1" como notificado. */
    paraBooleano(valor) {
      if (valor === true || valor === 1) return true;
      if (typeof valor === 'string') {
        const texto = valor.trim().toLowerCase();
        return texto === 'true' || texto === 't' || texto === '1';
      }
      return false;
    },
    somenteDigitos(valor) {
      if (valor === null || valor === undefined) return '';
      return String(valor).replace(/\D/g, '');
    },
    normalizarTexto(valor) {
      if (valor === null || valor === undefined) return '';
      return String(valor)
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .trim()
          .toUpperCase();
    },
    formatInteiro(valor) {
      const numero = Number(valor);
      return (isNaN(numero) ? 0 : numero).toLocaleString('pt-BR', {maximumFractionDigits: 0});
    },
    formatCnpj(valor) {
      const digitos = this.somenteDigitos(valor);
      if (digitos.length === 11) {
        return digitos.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
      }
      if (digitos.length === 14) {
        return digitos.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
      }
      return digitos || '—';
    },
    /** Escapa o texto usado no HTML do Swal. */
    escaparHtml(valor) {
      return String(valor === null || valor === undefined ? '' : valor)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
    },
    // ─── Competências ─────────────────────────────────────
    /**
     * Converte com segurança o campo `competencias` (string JSON, array ou nulo)
     * na lista de objetos {competencia, notificado, nts_codigo, nts_data},
     * ordenada cronologicamente e sem competências repetidas.
     */
    extrairCompetencias(valor) {
      if (valor === null || valor === undefined || valor === '') return [];

      let lista = valor;
      if (typeof lista === 'string') {
        const texto = lista.trim();
        if (!texto) return [];
        try {
          lista = JSON.parse(texto);
        } catch (error) {
          return [];
        }
      }
      if (!Array.isArray(lista)) return [];

      const porCompetencia = new Map();
      lista.forEach(item => {
        const normalizado = this.normalizarItemCompetencia(item);
        if (!normalizado) return;
        const anterior = porCompetencia.get(normalizado.competencia);
        // Na duplicidade, o registro notificado prevalece: nunca liberar
        // para seleção uma competência que já foi notificada.
        if (!anterior || (normalizado.notificado && !anterior.notificado)) {
          porCompetencia.set(normalizado.competencia, normalizado);
        }
      });

      return Array.from(porCompetencia.values())
          .sort((a, b) => this.chaveCompetencia(a.competencia) - this.chaveCompetencia(b.competencia));
    },
    /** Aceita o objeto novo da API e também a string simples do formato antigo. */
    normalizarItemCompetencia(item) {
      if (item === null || item === undefined) return null;

      const bruto = typeof item === 'object' ? item : {competencia: item};
      const competencia = this.normalizarCompetencia(bruto.competencia);
      if (!competencia) return null;

      const notificado = this.paraBooleano(bruto.notificado);
      const codigo = this.paraInteiroOuNulo(bruto.nts_codigo);
      return {
        competencia,
        notificado,
        nts_codigo: notificado ? codigo : null,
        nts_data: notificado ? this.textoOuNulo(bruto.nts_data) : null,
      };
    },
    normalizarCompetencia(valor) {
      if (valor === null || valor === undefined) return null;
      const texto = String(valor).trim();
      if (!texto) return null;

      let mes = null;
      let ano = null;

      const barra = texto.match(/^(\d{1,2})\/(\d{4})$/);      // MM/AAAA
      const traco = texto.match(/^(\d{4})-(\d{1,2})/);        // AAAA-MM[-DD]
      if (barra) {
        mes = Number(barra[1]);
        ano = Number(barra[2]);
      } else if (traco) {
        mes = Number(traco[2]);
        ano = Number(traco[1]);
      } else {
        return null;
      }

      if (!ano || mes < 1 || mes > 12) return null;
      return `${String(mes).padStart(2, '0')}/${ano}`;
    },
    /** Normaliza para MM/AAAA e descarta valores inválidos ou repetidos. */
    competenciasUnicas(lista) {
      const vistas = new Set();
      const unicas = [];
      (lista || []).forEach(item => {
        const competencia = this.normalizarCompetencia(item);
        if (competencia && !vistas.has(competencia)) {
          vistas.add(competencia);
          unicas.push(competencia);
        }
      });
      return unicas;
    },
    /** Ordena cronologicamente, da competência mais antiga para a mais recente. */
    ordenarCompetencias(competencias) {
      return (competencias || []).slice().sort((a, b) => this.chaveCompetencia(a) - this.chaveCompetencia(b));
    },
    chaveCompetencia(competencia) {
      const partes = String(competencia || '').split('/');
      const mes = Number(partes[0]) || 0;
      const ano = Number(partes[1]) || 0;
      return ano * 100 + mes;
    },
    /**
     * Agrupa as competências já notificadas por `nts_codigo`, em uma única
     * passagem, para que o mesmo número de notificação apareça uma só vez.
     */
    agruparNotificacoes(competencias) {
      const grupos = new Map();
      (competencias || []).forEach(item => {
        if (!item.notificado || item.nts_codigo === null) return;
        let grupo = grupos.get(item.nts_codigo);
        if (!grupo) {
          grupo = {nts_codigo: item.nts_codigo, nts_data: item.nts_data, competencias: []};
          grupos.set(item.nts_codigo, grupo);
        }
        if (!grupo.nts_data && item.nts_data) grupo.nts_data = item.nts_data;
        grupo.competencias.push(item.competencia);
      });

      return Array.from(grupos.values())
          .sort((a, b) => a.nts_codigo - b.nts_codigo)
          .map(grupo => ({...grupo, competenciasTexto: grupo.competencias.join(', ')}));
    },
    // ─── Seleção (fonte única: notificacoes_enviar) ───────
    competenciasSelecionadas(ecoCodigo) {
      const codigo = this.paraCodigo(ecoCodigo);
      const notificacao = this.notificacoes_enviar.find(item => item.eco_codigo === codigo);
      return notificacao ? notificacao.competencias.slice() : [];
    },
    totalSelecionadasDaEmpresa(registro) {
      const selecionadas = this.mapaSelecao[registro.eco_codigo];
      if (!selecionadas) return 0;
      return registro.pendentes.reduce(
          (total, competencia) => total + (selecionadas.has(competencia) ? 1 : 0), 0
      );
    },
    /**
     * Ponto único de escrita em `notificacoes_enviar`.
     * Descarta o que não estiver pendente naquela empresa — é a garantia de
     * que uma competência já notificada nunca entra na seleção —, remove a
     * empresa quando a lista final fica vazia e mantém as competências sem
     * duplicidade, ordenadas e o código econômico inteiro.
     */
    definirSelecao(ecoCodigo, competencias) {
      const codigo = this.paraCodigo(ecoCodigo);
      if (codigo === null) return;

      const pendentes = this.pendentesPorEmpresa[codigo];
      const finais = this.ordenarCompetencias(this.competenciasUnicas(competencias))
          .filter(competencia => !!pendentes && pendentes.has(competencia));
      const indice = this.notificacoes_enviar.findIndex(item => item.eco_codigo === codigo);

      if (!finais.length) {
        if (indice !== -1) this.notificacoes_enviar.splice(indice, 1);
        return;
      }
      const notificacao = {eco_codigo: codigo, competencias: finais};
      if (indice === -1) {
        this.notificacoes_enviar.push(notificacao);
      } else {
        this.notificacoes_enviar.splice(indice, 1, notificacao);
      }
    },
    alternarCompetencia(registro, item) {
      if (!registro.selecionavel || item.notificado) return;
      const atuais = this.competenciasSelecionadas(registro.eco_codigo);
      const novas = atuais.indexOf(item.competencia) !== -1
          ? atuais.filter(competencia => competencia !== item.competencia)
          : atuais.concat(item.competencia);
      this.definirSelecao(registro.eco_codigo, novas);
    },
    selecionarPendentesDaEmpresa(registro) {
      if (!registro.selecionavel) return;
      this.definirSelecao(registro.eco_codigo, registro.pendentes);
    },
    limparEmpresa(registro) {
      this.definirSelecao(registro.eco_codigo, []);
    },
    selecionarPendentesVisiveis() {
      this.registrosFiltrados.forEach(registro => this.selecionarPendentesDaEmpresa(registro));
    },
    limparSelecao() {
      this.notificacoes_enviar = [];
    },
    /**
     * Após recarregar a consulta, descarta seleções de empresas ou
     * competências que deixaram de estar pendentes (inclusive as que
     * acabaram de ser notificadas).
     */
    sincronizarSelecao() {
      if (!this.notificacoes_enviar.length) return;

      this.notificacoes_enviar = this.notificacoes_enviar
          .map(notificacao => {
            const pendentes = this.pendentesPorEmpresa[notificacao.eco_codigo];
            if (!pendentes) return null;
            const competencias = notificacao.competencias.filter(competencia => pendentes.has(competencia));
            return competencias.length ? {eco_codigo: notificacao.eco_codigo, competencias} : null;
          })
          .filter(Boolean);
    },
    // ─── Filtros e paginação ──────────────────────────────
    limparFiltros() {
      this.filtro.pes_nome = "";
      this.filtro.esr_cnpj = "";
      this.filtro.competencia = "";
      this.filtro.selecao = "todas";
      this.pagina = 1;
    },
    irParaPagina(numeroPagina) {
      this.pagina = parseInt(numeroPagina, 10) || 1;
      if (this.$refs.lista) this.$refs.lista.scrollIntoView({behavior: 'smooth', block: 'start'});
    },
    // ─── Envio ────────────────────────────────────────────
    /** Cópia imutável de `notificacoes_enviar`, pronta para a requisição. */
    montarPayload() {
      return this.notificacoes_enviar.map(notificacao => ({
        eco_codigo: notificacao.eco_codigo,
        competencias: notificacao.competencias.slice(),
      }));
    },
    closeModal(ref) {
      this.$refs[ref].hide();
    },
    openModal(ref) {
      this.$refs[ref].show();
    },
    enviarNotificacoes() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return false;
      }
      const payload = {
        lote: this.lote,
        empresas: []
      }
      payload.empresas = this.montarPayload();
      this.httpSend = true;
      save_nacional_notificacoes(payload).then(result => {
        if (result && result.status) {
          notifySucesso('Notificações', result.mensagem || 'Notificação gerada com sucesso');
          openDownloadFile(6, result.dados, 'notificacao')
          this.limparSelecao();
          this.closeModal('modal-gera-arquivo');
          this.pesquisar();
        } else {
          notifyErro('Notificações', (result && result.mensagem) || 'Erro ao salvar notificação');
        }
      }).catch(error => {
        console.error(error);
        notifyErro('Notificações', 'Falha de comunicação com o servidor ao gerar arquivo de notificações.');
      }).finally(() => {
        this.httpSend = false;
      });
    },
    // ─── Ícones ───────────────────────────────────────────
    iconEmpresa() {
      return `<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
              stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18"/><path d="M5 21V5a2 2 0 012-2h6a2 2 0 012 2v16"/>
              <path d="M15 9h4a2 2 0 012 2v10"/><path d="M9 7h2M9 11h2M9 15h2"/></svg>`;
    },
    iconCalendario() {
      return `<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
              stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="16" rx="2"/>
              <path d="M3 10h18M8 3v4M16 3v4"/></svg>`;
    },
    iconCadeado() {
      return `<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
              stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="10" width="16" height="11" rx="2"/>
              <path d="M8 10V7a4 4 0 018 0v3"/></svg>`;
    },
    iconAlerta() {
      return `<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
              stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/>
              <path d="M12 7v6M12 16h.01"/></svg>`;
    },
    iconCheck() {
      return `<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"
              stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>`;
    },
  },
};
</script>

<style scoped>
/* ─── Base ───────────────────────────────────────────────── */
.notif-sn {
  width: 100%;
  color: #0f172a;
}

/* ─── Barra superior ─────────────────────────────────────── */
.page-intro {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.25rem;
}

.page-intro__sub {
  margin: 0;
  font-size: 13px;
  color: #94a3b8;
}

.page-intro__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.badge-live {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 5px 12px;
}

.live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
}

.btn-atualizar {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.btn-atualizar:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #94a3b8;
}

.btn-atualizar:disabled {
  opacity: 0.6;
  cursor: default;
}

/* ─── Erro ───────────────────────────────────────────────── */
.alert-erro {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #fff8f8;
  border: 1px solid #fee2e2;
  border-left: 4px solid #ef4444;
  border-radius: 10px;
  padding: 14px 1.25rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
}

.alert-erro__icon {
  color: #dc2626;
  display: flex;
  flex-shrink: 0;
}

.alert-erro__body {
  flex: 1;
  min-width: 200px;
}

.alert-erro__title {
  margin: 0 0 2px;
  font-size: 13px;
  font-weight: 600;
  color: #991b1b;
}

.alert-erro__desc {
  margin: 0;
  font-size: 12px;
  color: #b91c1c;
}

.alert-erro__btn {
  background: #dc2626;
  border: 1px solid #dc2626;
  border-radius: 7px;
  color: #fff;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.alert-erro__btn:disabled {
  opacity: 0.6;
  cursor: default;
}

/* ─── Skeleton ───────────────────────────────────────────── */
.skeleton-wrap {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.sk-metrics {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
}

.sk-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.sk-metric,
.sk-card {
  border-radius: 12px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 37%, #f1f5f9 63%);
  background-size: 400% 100%;
  animation: sk-load 1.4s ease infinite;
}

.sk-metric {
  height: 92px;
}

.sk-card {
  height: 210px;
}

@keyframes sk-load {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

/* ─── Indicadores ────────────────────────────────────────── */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.metric-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fff;
  border: 0.5px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem 1.15rem;
}

.metric-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  flex-shrink: 0;
  background: #f1f5f9;
  color: #64748b;
}

.metric-card--blue .metric-icon {
  background: #eff6ff;
  color: #2563eb;
}

.metric-card--teal .metric-icon {
  background: #ecfdf5;
  color: #0d9488;
}

.metric-card--amber .metric-icon {
  background: #fffbeb;
  color: #d97706;
}

.metric-card--indigo .metric-icon {
  background: #eef2ff;
  color: #4f46e5;
}

.metric-card--slate .metric-icon {
  background: #f1f5f9;
  color: #475569;
}

.metric-body {
  min-width: 0;
}

.metric-label {
  margin: 0 0 2px;
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}

.metric-value {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #0f172a;
  line-height: 1.2;
}

.metric-sub {
  margin: 2px 0 0;
  font-size: 11px;
  color: #94a3b8;
}

/* ─── Cards ──────────────────────────────────────────────── */
.table-card {
  background: #fff;
  border: 0.5px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.mb-section {
  margin-bottom: 1.25rem;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.1rem 1.5rem;
  border-bottom: 0.5px solid #f1f5f9;
  flex-wrap: wrap;
  gap: 8px;
}

.header-left-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-right-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.chart-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.badge-count {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 2px 9px;
}

.badge-count--ativo {
  color: #3b55e0;
  background: #eef2ff;
}

.btn-acao {
  background: #fff;
  color: #475569;
  border: 0.5px solid #e2e8f0;
  border-radius: 7px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.btn-acao:hover:not(:disabled) {
  background: #f8fafc;
  color: #0f172a;
  border-color: #cbd5e1;
}

.btn-acao:disabled {
  opacity: 0.5;
  cursor: default;
}

/* ─── Filtros ────────────────────────────────────────────── */
.filtros-body {
  padding: 1.25rem 1.5rem;
}

.filtros-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
}

.campo {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.campo label {
  margin: 0;
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.campo-input {
  width: 100%;
  border: 0.5px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  color: #334155;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.campo-input::placeholder {
  color: #cbd5e1;
}

.campo-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
}

.filtros-acoes {
  display: flex;
  gap: 10px;
  margin-top: 1.25rem;
}

.btn-pesquisar {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #4f6ef7;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
}

.btn-pesquisar:hover {
  background: #3b55e0;
}

.btn-pesquisar:active {
  transform: scale(0.98);
}

.btn-limpar {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: #64748b;
  border: 0.5px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.btn-limpar:hover {
  background: #f8fafc;
  color: #334155;
}

/* ─── Empresas ───────────────────────────────────────────── */
.empresas-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  padding: 1.25rem 1.5rem;
}

.empresa-card {
  display: flex;
  flex-direction: column;
  border: 0.5px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}

.empresa-card:hover {
  border-color: #cbd5e1;
}

.empresa-card--ativa {
  border-color: #c7d2fe;
  box-shadow: inset 3px 0 0 #4f6ef7;
  background: #fbfcff;
}

.empresa-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding: 0.9rem 1.1rem;
  border-bottom: 0.5px solid #f1f5f9;
}

.empresa-card__id {
  min-width: 0;
}

.empresa-card__nome {
  margin: 0 0 2px;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empresa-card__dados {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin: 0;
  font-size: 11.5px;
  color: #64748b;
  font-variant-numeric: tabular-nums;
}

.empresa-card__sep {
  color: #cbd5e1;
}

/* ─── Resumo por empresa ─────────────────────────────────── */
.empresa-resumo {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: #f1f5f9;
  border-bottom: 0.5px solid #f1f5f9;
}

.empresa-resumo__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  padding: 8px 6px;
  background: #fcfdfe;
}

.empresa-resumo__valor {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
}

.empresa-resumo__rotulo {
  font-size: 10px;
  font-weight: 500;
  color: #94a3b8;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.empresa-resumo__item--notificadas .empresa-resumo__valor {
  color: #0d9488;
}

.empresa-resumo__item--pendentes .empresa-resumo__valor {
  color: #d97706;
}

.badge-selecao {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 3px 10px;
  font-variant-numeric: tabular-nums;
}

.badge-selecao--ativa {
  color: #fff;
  background: #4f6ef7;
}

.empresa-card__body {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0.9rem 1.1rem 1rem;
}

.empresa-card__subheader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.empresa-card__legenda {
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.empresa-card__acoes {
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-link {
  background: none;
  border: none;
  padding: 0;
  font-size: 11.5px;
  font-weight: 500;
  color: #4f6ef7;
  cursor: pointer;
}

.btn-link:hover:not(:disabled) {
  text-decoration: underline;
}

.btn-link:disabled {
  color: #cbd5e1;
  cursor: default;
}

.btn-link__sep {
  color: #cbd5e1;
  font-size: 11px;
}

.competencias-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.comp-chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  padding: 5px 11px 5px 8px;
  border: 0.5px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.12s, background 0.12s, color 0.12s;
}

.comp-chip:hover {
  border-color: #c7d2fe;
  background: #f8fafc;
}

.comp-chip--ativa {
  border-color: #4f6ef7;
  background: #eef2ff;
  color: #3b55e0;
}

.comp-chip__input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
  margin: 0;
}

.comp-chip__box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  background: #fff;
  color: transparent;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}

.comp-chip--ativa .comp-chip__box {
  background: #4f6ef7;
  border-color: #4f6ef7;
  color: #fff;
}

.comp-chip__input:focus-visible + .comp-chip__box {
  box-shadow: 0 0 0 3px rgba(79, 110, 247, 0.25);
}

.comp-chip__label {
  font-variant-numeric: tabular-nums;
}

.empresa-card__footer {
  font-size: 11.5px;
  color: #94a3b8;
  border-top: 0.5px solid #f1f5f9;
  padding-top: 8px;
}

.empresa-card__footer strong {
  color: #334155;
}

.empresa-card__aviso {
  color: #b45309;
}

.comp-chip--bloqueada {
  cursor: not-allowed;
}

.comp-chip--notificada {
  border-color: #f1f5f9;
  background: #f8fafc;
  color: #94a3b8;
}

.comp-chip--notificada:hover {
  border-color: #f1f5f9;
  background: #f8fafc;
}

.comp-chip--notificada .comp-chip__box {
  background: #cbd5e1;
  border-color: #cbd5e1;
  color: #fff;
}

/* Empresa sem código econômico: pendente, porém não notificável */
.comp-chip--bloqueada:not(.comp-chip--notificada) {
  opacity: 0.55;
}

.comp-chip--bloqueada:not(.comp-chip--notificada):hover {
  border-color: #e2e8f0;
  background: #fff;
}

.comp-chip__status {
  font-size: 9.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 1px 7px;
}

.comp-chip__status--pendente {
  color: #b45309;
  background: #fffbeb;
}

.comp-chip__status--notificada {
  color: #64748b;
  background: #eef2f6;
}

/* ─── Notificações já realizadas ─────────────────────────── */
.notificacoes-bloco {
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding-top: 10px;
  border-top: 0.5px solid #f1f5f9;
}

.notificacao-item {
  border: 0.5px solid #e2e8f0;
  border-left: 3px solid #0d9488;
  border-radius: 8px;
  background: #f8fafc;
  padding: 8px 11px;
}

.notificacao-item__topo {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

.notificacao-item__numero {
  font-size: 12px;
  font-weight: 600;
  color: #0f172a;
}

.notificacao-item__data {
  font-size: 11px;
  color: #64748b;
  font-variant-numeric: tabular-nums;
}

.notificacao-item__competencias {
  margin: 3px 0 0;
  font-size: 11.5px;
  color: #475569;
  line-height: 1.5;
  font-variant-numeric: tabular-nums;
}

.empresa-card__vazia {
  padding: 1.1rem;
  font-size: 12px;
  color: #94a3b8;
}

/* ─── Estado vazio e rodapé ──────────────────────────────── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 3rem 1.5rem;
  color: #94a3b8;
}

.empty-state p {
  margin: 0;
  font-size: 13px;
}

.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 0.9rem 1.5rem;
  border-top: 0.5px solid #f1f5f9;
}

.table-footer__info {
  font-size: 12px;
  color: #94a3b8;
}

/* ─── Barra de envio ─────────────────────────────────────── */
.action-bar {
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-top: 1.25rem;
  padding: 0.9rem 1.5rem;
  background: #fff;
  border: 0.5px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 -4px 16px rgba(15, 23, 42, 0.06);
}

.action-bar__resumo {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 12.5px;
  color: #64748b;
}

.resumo-item strong {
  color: #0f172a;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.resumo-sep {
  width: 1px;
  height: 14px;
  background: #e2e8f0;
}

.btn-enviar {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #4f6ef7;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 9px 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
}

.btn-enviar:hover:not(:disabled) {
  background: #3b55e0;
}

.btn-enviar:active:not(:disabled) {
  transform: scale(0.98);
}

.btn-enviar:disabled {
  background: #e2e8f0;
  color: #94a3b8;
  cursor: default;
}

.btn-enviar__badge {
  background: rgba(255, 255, 255, 0.22);
  border-radius: 999px;
  padding: 1px 8px;
  font-size: 11.5px;
  font-variant-numeric: tabular-nums;
}

/* ─── Responsivo ─────────────────────────────────────────── */
@media (max-width: 1399px) {
  .metrics-grid,
  .sk-metrics {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 1199px) {
  .empresas-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 991px) {
  .metrics-grid,
  .sk-metrics {
    grid-template-columns: repeat(2, 1fr);
  }

  /* Em telas menores as competências viram lista vertical */
  .competencias-grid {
    flex-direction: column;
    align-items: stretch;
  }

  .comp-chip {
    justify-content: flex-start;
  }

  .comp-chip__status {
    margin-left: auto;
  }
}

@media (max-width: 991px) {
  .filtros-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .empresas-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .sk-cards {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 575px) {
  .filtros-grid {
    grid-template-columns: 1fr;
  }

  .metrics-grid,
  .sk-metrics {
    grid-template-columns: 1fr;
  }

  .filtros-acoes {
    flex-direction: column;
  }

  .btn-pesquisar,
  .btn-limpar {
    justify-content: center;
  }

  .empresas-grid {
    padding: 1rem;
  }

  .action-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-enviar {
    justify-content: center;
  }
}
</style>
